/**
 * Persistent project environments.
 *
 * An environment is the long-lived workspace of one `(user, project)` pair: a
 * git worktree that is never garbage-collected, a preview on a stable hostname,
 * and a sandbox database whose test data accumulates across conversations.
 *
 * The point is a change of ownership. Today the worktree, the preview and the
 * sandbox database all belong to a *thread*, so archiving a conversation
 * destroys them — acceptable for a developer who treats a thread as a branch,
 * fatal for a product manager whose thread is just a chat window over an app
 * they have been filling with test data for a week. Here the owner is the
 * environment, and threads merely point at it through
 * `OrchestrationThread.environmentId`.
 *
 * The branch is the environment's *identity*, not its content: it is created
 * once and stays, while the code inside it moves under it on request (fetch a
 * colleague's PR, go back to base). That is why `baseRef` is recorded — it is
 * the ref to return to, and it is not derivable from the worktree afterwards.
 *
 * @module environments
 */
import { Schema } from "effect";

import {
  EnvironmentId,
  IsoDateTime,
  NonNegativeInt,
  PositiveInt,
  ProjectId,
  ThreadId,
  TrimmedNonEmptyString,
  UserId,
} from "./baseSchemas";

/**
 * Lifecycle of an environment.
 *
 * Provisioning is slow — the worktree itself costs about a second, but the
 * project's `onWorktreeCreate` hook (install, migrations, seed) can run for
 * minutes — so the row exists from the moment it is requested and carries its
 * own state instead of appearing only once the work succeeded.
 *
 * A `failed` environment still occupies the one-per-`(user, project)` slot on
 * purpose: it holds a half-built worktree that has to be cleaned up. Retrying
 * is destroy-then-provision, which is also what frees the slot.
 */
export const ProjectEnvironmentStatus = Schema.Literals(["provisioning", "ready", "failed"]);
export type ProjectEnvironmentStatus = typeof ProjectEnvironmentStatus.Type;

/**
 * Who an environment belongs to.
 *
 * - `personal`: the workspace of one `(user, project)` pair, as above. The
 *   original and still the common case.
 * - `project`: a shared environment with no human owner — QA, staging — named
 *   instead of attributed, tracking a branch of the project and outliving
 *   everyone who talks to it.
 *
 * The distinction is not cosmetic: it decides which uniqueness rule applies
 * (one per pair vs one per name), whether `userId` may be null, and whether the
 * server keeps a synchronisation state for the row at all.
 */
export const ProjectEnvironmentKind = Schema.Literals(["personal", "project"]);
export type ProjectEnvironmentKind = typeof ProjectEnvironmentKind.Type;

export const DEFAULT_PROJECT_ENVIRONMENT_KIND: ProjectEnvironmentKind = "personal";

/**
 * How the environment's worktree stands against the branch it tracks.
 *
 * Computed by the server from `git rev-list`, never guessed:
 *
 * ```
 * behind = rev-list --count HEAD..origin/<trackedBranch>
 * ahead  = rev-list --count origin/<trackedBranch>..HEAD
 * in-sync  ⇔ behind = 0 ∧ ahead = 0
 * behind   ⇔ behind > 0 ∧ ahead = 0
 * diverged ⇔ ahead > 0
 * unknown  ⇔ the fetch failed
 * ```
 *
 * `unknown` exists so that a repository the server cannot reach — no network,
 * no credential — never reads as "up to date". Reporting freshness from an
 * absence of data is the one failure this state machine is built to prevent.
 */
export const ProjectEnvironmentSyncState = Schema.Literals([
  "in-sync",
  "behind",
  "diverged",
  "unknown",
]);
export type ProjectEnvironmentSyncState = typeof ProjectEnvironmentSyncState.Type;

export const DEFAULT_PROJECT_ENVIRONMENT_SYNC_STATE: ProjectEnvironmentSyncState = "unknown";

/**
 * What was changed inside an environment, as opposed to what code it runs.
 *
 * `data` is rows — a fixture loaded, a record corrected, a snapshot restored.
 * `config` is the environment file the project declares in `qa.envFile`. They
 * are kept apart because they are undone by different things: data by restoring
 * a snapshot, configuration by the copy kept in `.taskin/env-history/`.
 */
export const ProjectEnvironmentModificationKind = Schema.Literals(["data", "config"]);
export type ProjectEnvironmentModificationKind = typeof ProjectEnvironmentModificationKind.Type;

/**
 * Names of project environments are slugs: lowercase, digits and single dashes.
 *
 * They travel into a branch name (`env/<name>`) and into a preview hostname
 * (`<name>-<project>.prev…`), so anything that is not URL- and ref-safe would
 * fail far from where it was typed.
 */
export const PROJECT_ENVIRONMENT_NAME_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const PROJECT_ENVIRONMENT_NAME_MAX_CHARS = 32;

/**
 * A project environment's name, validated.
 *
 * Used by commands and by the UI. The projected row below deliberately stores
 * the name as plain nullable text instead of this: a projection must decode
 * whatever was once written, and a row that fails to decode takes the whole
 * read model with it.
 */
export const ProjectEnvironmentName = TrimmedNonEmptyString.check(
  Schema.isMaxLength(PROJECT_ENVIRONMENT_NAME_MAX_CHARS),
  Schema.isPattern(PROJECT_ENVIRONMENT_NAME_PATTERN),
);
export type ProjectEnvironmentName = typeof ProjectEnvironmentName.Type;

/** Which code a clone starts from. */
export const EnvironmentCloneCodeMode = Schema.Literals(["same-sha", "branch-tip"]);
export type EnvironmentCloneCodeMode = typeof EnvironmentCloneCodeMode.Type;

/** Which data a clone starts from. */
export const EnvironmentCloneDataMode = Schema.Literals([
  "fresh-snapshot",
  "latest-snapshot",
  "baseline",
  "none",
]);
export type EnvironmentCloneDataMode = typeof EnvironmentCloneDataMode.Type;

export const DEFAULT_ENVIRONMENT_CLONE_CODE_MODE: EnvironmentCloneCodeMode = "same-sha";
export const DEFAULT_ENVIRONMENT_CLONE_DATA_MODE: EnvironmentCloneDataMode = "fresh-snapshot";

/**
 * "Create `staging` exactly like `qa`" — the request form.
 *
 * Copying only the branch is not copying an environment: what makes `qa` useful
 * is the commit it sits on, the rows in its database and the values in its
 * `.env`, and a clone that reproduced one of the three would be a different
 * environment wearing the same name. Origin and destination must belong to the
 * same project.
 */
export const EnvironmentCloneRequest = Schema.Struct({
  environmentId: EnvironmentId,
  code: EnvironmentCloneCodeMode.pipe(
    Schema.withDecodingDefault(() => DEFAULT_ENVIRONMENT_CLONE_CODE_MODE),
  ),
  data: EnvironmentCloneDataMode.pipe(
    Schema.withDecodingDefault(() => DEFAULT_ENVIRONMENT_CLONE_DATA_MODE),
  ),
  /** Copy the project's `inheritFiles`, rewriting only `TASKIN_*` keys. */
  config: Schema.Boolean.pipe(Schema.withDecodingDefault(() => true)),
});
export type EnvironmentCloneRequest = typeof EnvironmentCloneRequest.Type;

/**
 * What a clone actually did, recorded on the provisioning event.
 *
 * The request plus the commit that was resolved at the time: `branch-tip` means
 * a different SHA tomorrow, and "which commit did `staging` start from?" has to
 * be answerable a month later, when the origin may not exist any more.
 */
export const EnvironmentCloneOrigin = Schema.Struct({
  ...EnvironmentCloneRequest.fields,
  sha: Schema.NullOr(TrimmedNonEmptyString),
});
export type EnvironmentCloneOrigin = typeof EnvironmentCloneOrigin.Type;

/**
 * One database copy taken before a pull request was merged into the environment.
 *
 * Both halves are kept because they answer different questions. The `label` is
 * what a person reads in the snapshot list (`trial-pr-131`); the `snapshotId` is
 * what "go back to base" restores, and it is exact — restoring *by label*
 * resolves to the most recent copy carrying it, which is the wrong copy the
 * moment the same pull request is tried twice.
 */
export const EnvironmentTrialSnapshot = Schema.Struct({
  engine: Schema.Literals(["postgres", "mysql"]),
  snapshotId: TrimmedNonEmptyString,
  label: TrimmedNonEmptyString,
});
export type EnvironmentTrialSnapshot = typeof EnvironmentTrialSnapshot.Type;

/**
 * A pull request being tried *in place* on a project environment.
 *
 * The in-place trial (F3) adds no entity: the environment itself carries the
 * pull request it is currently wearing, and the whole state machine is "there
 * is one, or there is not". One indivisible record rather than six columns, for
 * the reason 049 gives about `linked_pull_request_json`: half a trial — a
 * number with no snapshots to go back to, snapshots belonging to no number — is
 * a state nothing could act on, so it is made unrepresentable instead of merely
 * refused.
 *
 * `snapshots` is what makes the trial reversible and is therefore the reason the
 * record exists at all: the merge brings a migration that rewrites the PM's
 * data, and "reject the PR" has to mean the data comes back too, not only the
 * code.
 */
export const EnvironmentActiveTrial = Schema.Struct({
  prNumber: PositiveInt,
  /** The commit of `refs/pull/<n>/head` that was merged in. */
  headSha: TrimmedNonEmptyString,
  startedAt: IsoDateTime,
  /** The human it was started for. Null when auth is off. */
  startedBy: Schema.NullOr(UserId),
  /** The conversation it was started from, which the card links to. */
  threadId: Schema.NullOr(ThreadId),
  /** The copies taken before the merge, one per engine with a database. */
  snapshots: Schema.Array(EnvironmentTrialSnapshot),
});
export type EnvironmentActiveTrial = typeof EnvironmentActiveTrial.Type;

/**
 * Fields absent from rows written before project environments existed decode to
 * the reading those rows always had: a personal environment of one user, with
 * no branch tracked and nothing known about its freshness.
 */
const nullableText = Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
  Schema.withDecodingDefault(() => null),
);

export const ProjectEnvironment = Schema.Struct({
  id: EnvironmentId,
  projectId: ProjectId,
  /**
   * Owner of the environment. Exactly one live environment per (user, project).
   *
   * Null if and only if `kind` is `project`: a shared environment answers to
   * the project, and attributing it to whoever created it would make its
   * lifetime depend on that person's account.
   */
  userId: Schema.NullOr(UserId),
  kind: ProjectEnvironmentKind.pipe(
    Schema.withDecodingDefault(() => DEFAULT_PROJECT_ENVIRONMENT_KIND),
  ),
  /**
   * Slug identifying a project environment (`qa`, `staging`). Null for personal
   * ones, which are identified by their owner. Unique among the live project
   * environments of a project.
   */
  name: nullableText,
  /**
   * The branch this environment follows, copied from `project.baseBranch` when
   * it was created. Stored on the environment rather than read from the project
   * on every check, so that changing the project's base branch does not silently
   * move every environment underneath the people using them.
   */
  trackedBranch: nullableText,
  /** SHA of the tracked branch after the last successful sync. */
  lastSyncedSha: nullableText,
  status: ProjectEnvironmentStatus,
  /**
   * The environment's permanent branch.
   *
   * Deliberately outside the naming scheme of temporary worktree branches, so
   * that the automatic rename of a thread's branch can never rewrite it.
   */
  branch: TrimmedNonEmptyString,
  /** Absolute path of the worktree. Stable for the environment's whole life. */
  worktreePath: TrimmedNonEmptyString,
  /**
   * Ref the worktree was branched from, and the one `git_reset_to_base` returns
   * to. Recorded because the worktree cannot tell us afterwards which ref it
   * started at.
   */
  baseRef: TrimmedNonEmptyString,
  /** Preview hostname, allocated by the dev server manager. Null until started. */
  devHostname: Schema.NullOr(TrimmedNonEmptyString),
  /** Preview port, sticky across restarts so a shared URL keeps working. */
  devPort: Schema.NullOr(NonNegativeInt),
  /**
   * Which dev server to run, and the command to run it with, remembered on the
   * environment rather than on whichever thread started it last: several
   * conversations share one workspace, and the owner presses a button rather
   * than choosing a framework.
   */
  devFrameworkId: Schema.NullOr(TrimmedNonEmptyString),
  devCommandOverride: Schema.NullOr(TrimmedNonEmptyString),
  /**
   * When public access to this environment's preview expires, or null while it
   * is private. Optional so a row written before the column existed still
   * decodes as what it was: private.
   */
  devPublicUntil: Schema.optional(Schema.NullOr(IsoDateTime)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  /**
   * How this environment stands against `trackedBranch`, as of `syncCheckedAt`.
   *
   * Server-computed and never editable. Defaults to `unknown` — including for
   * every row written before the column existed — because the honest answer
   * before the first `git fetch` is "nobody has looked".
   */
  syncState: ProjectEnvironmentSyncState.pipe(
    Schema.withDecodingDefault(() => DEFAULT_PROJECT_ENVIRONMENT_SYNC_STATE),
  ),
  /** Commits of `origin/<trackedBranch>` missing here. Null while unknown. */
  behindBy: Schema.optional(Schema.NullOr(NonNegativeInt)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  /**
   * When `syncState` was last computed. Shown next to the state so a stale
   * reading is legible as stale instead of passing for live.
   */
  syncCheckedAt: Schema.optional(Schema.NullOr(IsoDateTime)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  /** When the worktree last moved onto its tracked branch, and at whose hand. */
  lastSyncAt: Schema.optional(Schema.NullOr(IsoDateTime)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  lastSyncBy: Schema.optional(Schema.NullOr(UserId)).pipe(Schema.withDecodingDefault(() => null)),
  /**
   * The environment this one was cloned from, and the commit it was cloned at.
   * Kept for the card ("cloned from `qa` on …"), and because `branch-tip` makes
   * the SHA underivable afterwards.
   */
  clonedFromEnvironmentId: Schema.optional(Schema.NullOr(EnvironmentId)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  clonedFromSha: nullableText,
  /**
   * The last write to this environment's data or configuration, and where it
   * came from.
   *
   * Four columns rather than a join to an audit table: the card shows one line,
   * every reader of the environment already loads this row, and the history
   * that matters beyond "who touched it last" is the thread itself — which is
   * why the thread id is here and is what the line links to.
   */
  lastModifiedAt: Schema.optional(Schema.NullOr(IsoDateTime)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  lastModifiedBy: Schema.optional(Schema.NullOr(UserId)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  lastModifiedThreadId: Schema.optional(Schema.NullOr(ThreadId)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  /** What that write touched, or null when nothing has been written yet. */
  lastModifiedKind: Schema.optional(Schema.NullOr(ProjectEnvironmentModificationKind)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  /**
   * The pull request this environment is currently wearing, or null.
   *
   * While it is set the environment is *not* what its branch says it is, so
   * every reader has to prefer this over `syncState`: the card shows "Probando
   * PR #131" rather than "divergido", and `sync-base` refuses rather than
   * burying somebody's trial under a fast-forward.
   */
  activeTrial: Schema.optional(Schema.NullOr(EnvironmentActiveTrial)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  /** Why provisioning failed; null in every other state. */
  failureReason: Schema.NullOr(TrimmedNonEmptyString),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  /** Set when destroyed. A destroyed environment frees the slot of its pair. */
  deletedAt: Schema.NullOr(IsoDateTime),
});
export type ProjectEnvironment = typeof ProjectEnvironment.Type;
